/**
 * Herd: identity, lifecycle (in, out, death, cull, missing), identifiers, ownership and rights, lots
 * and head counts. Every fact is an event; status, lot and ownership are projections of them.
 */
import {
  ANIMAL_CLASS, defineEvent, defineFacet, defineModule, f, msg,
  type AnimalProjection, type Badge, type EventLike, type Fact, type FollowUp, type Formatters, type Issue, type SubjectRef,
} from "../../contract";
import {
  discrepancy, reduceHerd, reduceLot, reducePlace,
  type ArrivalData, type CountData, type CullData, type DeathData, type DepartureData, type FoundData, type HerdFacet, type IdentifierData,
  type LotData, type LotFacet, type MissingData, type OwnershipData, type PlaceFacet, type WeaningData,
} from "./shared/lifecycle";
import {
  ARRIVAL_KIND, CAUSE_BASIS, CLASS_COUNT, CULL_DECISION, CULL_REASON, DEATH_CAUSE, DEPARTURE_KIND, DISPOSAL, EAR, ID_ACTION, ID_SCHEME, INTEREST,
  LOT_ACTION, LOT_PURPOSE, OWNERSHIP_BASIS, PRICE_BASIS, SALE_REASON, TAG_REASON, WEAN_METHOD,
} from "./shared/vocab";

export type { HerdFacet, LotFacet, PlaceFacet } from "./shared/lifecycle";

// ---------------------------------------------------------------- summary helpers

const ARRIVAL_VERB: Record<string, string> = {
  purchased: "Bought {{what}}", received: "Received {{what}}", leased_in: "Leased in {{what}}", returned: "{{what}} came back", born_elsewhere: "{{what}} came in, born elsewhere",
};
const DEPARTURE_VERB: Record<string, string> = {
  sold: "Sold {{what}}", shipped: "Shipped {{what}}", transferred: "Transferred {{what}}", leased_out: "Leased out {{what}}", slaughtered: "Butchered {{what}}",
};
const ARRIVAL_FACT: Record<string, string> = {
  purchased: "Bought {{date}}", received: "Received {{date}}", leased_in: "Leased in {{date}}", returned: "Came back {{date}}", born_elsewhere: "Born elsewhere, came {{date}}",
};
const DEPARTURE_FACT: Record<string, string> = {
  sold: "Sold {{date}}", shipped: "Shipped {{date}}", transferred: "Transferred {{date}}", leased_out: "Leased out {{date}}", slaughtered: "Butchered {{date}}",
};

function labelOf(e: Pick<EventLike, "subjects">, x: Formatters, role: string): string {
  const s = e.subjects.find((r) => r.role === role);
  return s ? s.label ?? x.label(s.id) : "";
}

/** "42, 17" for a few animals, "20 steers" for many or for counted lots. */
function what(e: Pick<EventLike, "subjects">, x: Formatters, role = "animal", head?: number, cls?: string): string {
  const animals = e.subjects.filter((s) => s.role === role && s.kind === "animal");
  if (animals.length && animals.length <= 6 && (head === undefined || head === animals.length)) return x.list(animals.map((s) => s.label ?? x.label(s.id)));
  const n = head ?? animals.length;
  const [one, other] = CLASS_COUNT[cls ?? "other"] ?? CLASS_COUNT.other;
  return x.count(n, one, other);
}

const join = (lead: string, details: Array<string | false | null | undefined>) => [lead, ...details.filter(Boolean)].join(" · ");

/** Pick the animals, or a lot with a head count. */
function needsAnimalsOrHead(data: { head?: number }, subjects: SubjectRef[]): Issue[] {
  const animals = subjects.filter((s) => s.role === "animal").length;
  const lot = subjects.some((s) => s.role === "lot");
  if (!animals && !(lot && typeof data.head === "number")) return [{ path: "subjects.animal", message: "Pick the animals, or a lot and how many head." }];
  if (typeof data.head === "number" && data.head < animals) return [{ path: "data.head", message: "Head can't be fewer than the animals picked." }];
  return [];
}

// ---------------------------------------------------------------- events

const lifecycleSubjects = (contactRole: "from" | "to", contactLabel: string) => [
  { role: "animal", kind: "animal" as const, label: "Animals", many: true },
  { role: "lot", kind: "group" as const, label: "Lot", help: "For cattle counted as a bunch rather than listed one by one" },
  { role: contactRole, kind: "contact" as const, label: contactLabel },
];

const arrival = defineEvent<ArrivalData>({
  type: "herd.arrival",
  label: "Arrival",
  description: "Cattle came onto the ranch: bought, received, leased in, back home, or born somewhere else.",
  icon: "arrive",
  tone: "herd",
  subjects: lifecycleSubjects("from", "Bought from or sent by"),
  fields: {
    kind: f.enum("How they came", ARRIVAL_KIND, { required: true, agent: "purchased: bought. received: someone else's cattle that came in (custom or pasture cattle) or a gift. leased_in: leased bulls or cows. returned: our cattle back from a lease, show or feedyard. born_elsewhere: calves born on another place." }),
    head: f.int("Head", { min: 1, help: "How many head came in. Needed when they aren't listed one by one.", agent: "Number of head. Required when the animals are not individually listed (then use a lot subject)." }),
    class: f.enum("What kind", ANIMAL_CLASS, { level: "more", agent: "Class of cattle when stated: steer, heifer, cow, bull, calf, yearling..." }),
    avg_weight: f.quantity("Average weight", "weight", { level: "more", help: "Pay weight from the ticket. Individual weights belong in a weighing.", agent: "Average or pay weight only when stated ('avg 550' is 550 lb)." }),
    price: f.money("Price", { level: "more", help: "Kept with the record. The bill itself belongs in Finances." }),
    price_basis: f.enum("Price is", PRICE_BASIS, { level: "more", agent: "per_cwt for prices like '$285' on calves, per_lb for '$2.85', per_head or total when said" }),
    owner_id: f.ref("Owned by", "contact", { level: "more", help: "Only when someone else owns them, like custom or pasture cattle" }),
    origin: f.text("Where they came from", { level: "more", help: "Ranch, town or state of origin" }),
    lease_until: f.date("Lease ends", { level: "more" }),
    reference: f.text("Ticket or invoice number", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: needsAnimalsOrHead,
  summary: (e, x) => {
    const d = e.data;
    const from = labelOf(e, x, "from");
    const lot = labelOf(e, x, "lot");
    const lead = [x.t(ARRIVAL_VERB[d.kind] ?? "Bought {{what}}", { what: what(e, x, "animal", d.head, d.class) }), from && x.t("from {{name}}", { name: from }), lot && x.t("into {{lot}}", { lot })].filter(Boolean).join(" ");
    return join(lead, [d.avg_weight && x.t("avg {{weight}}", { weight: x.quantity(d.avg_weight) }), d.lease_until && x.t("lease ends {{date}}", { date: x.date(d.lease_until) })]);
  },
  review: "always",
  agent: {
    when: "Cattle came onto the ranch: bought, received, leased in, came back, or born elsewhere. For cattle without individual tags, create a group with kind lot (name it for the seller and date) and put it in the lot role with head; never invent tags or animals. A sale barn or seller goes in the from role (look it up with find_records contact).",
    avoid: "Not for calves born here (breeding calving). Not for moving cattle between our own pastures (land move).",
    examples: ["bought 20 steers at Lampasas, avg 550", "picked up 3 bred heifers from Bob Jones, $2,400 a head", "Domino came back from the Moreno lease"],
  },
  effects: {
    followUps: (d, _on, subjects) => d.kind === "leased_in" && d.lease_until
      ? [{ key: "lease_end", title: msg("Leased cattle go back today"), due_at: d.lease_until, ...(subjects.find((s) => s.kind === "animal") ? { subject: { kind: "animal" as const, id: subjects.find((s) => s.kind === "animal")!.id } } : {}) }]
      : [],
  },
});

const departure = defineEvent<DepartureData>({
  type: "herd.departure",
  label: "Sale or departure",
  description: "Cattle left the herd: sold, shipped, transferred, leased out or butchered.",
  icon: "truck",
  tone: "sale",
  subjects: lifecycleSubjects("to", "Sold or sent to"),
  fields: {
    kind: f.enum("How they left", DEPARTURE_KIND, { required: true, agent: "sold: sold to a buyer or at a sale barn. shipped: sent somewhere but still ours (feedyard, grower). transferred: given or moved to another owner without a sale. leased_out: leased to someone. slaughtered: butchered (home or custom kill)." }),
    head: f.int("Head", { min: 1, help: "How many head left. Needed when they aren't listed one by one.", agent: "Number of head when the animals are not individually listed (with a lot subject)." }),
    reason: f.enum("Why", SALE_REASON, { level: "more", agent: "cull when they are culls or on the cull list" }),
    class: f.enum("What kind", ANIMAL_CLASS, { level: "more" }),
    avg_weight: f.quantity("Average weight", "weight", { level: "more", help: "Pay weight from the ticket" }),
    price: f.money("Price", { level: "more", help: "Kept with the record. The invoice itself belongs in Finances." }),
    price_basis: f.enum("Price is", PRICE_BASIS, { level: "more" }),
    destination: f.text("Where they went", { level: "more", help: "Sale barn, feedyard, packer or ranch" }),
    leaves_on: f.date("Picked up on", { level: "more", help: "When the truck comes, if that's after the sale" }),
    lease_until: f.date("Lease ends", { level: "more" }),
    reference: f.text("Ticket or invoice number", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: needsAnimalsOrHead,
  summary: (e, x) => {
    const d = e.data;
    const to = labelOf(e, x, "to");
    const lot = labelOf(e, x, "lot");
    const lead = [x.t(DEPARTURE_VERB[d.kind] ?? "Sold {{what}}", { what: what(e, x, "animal", d.head, d.class) }), lot && x.t("from {{name}}", { name: lot }), to ? x.t("to {{name}}", { name: to }) : d.destination ? x.t("to {{name}}", { name: d.destination }) : ""].filter(Boolean).join(" ");
    return join(lead, [d.reason && x.t(SALE_REASON[d.reason as keyof typeof SALE_REASON] ?? d.reason), d.avg_weight && x.t("avg {{weight}}", { weight: x.quantity(d.avg_weight) }),
      d.leaves_on && x.t("picked up {{date}}", { date: x.date(d.leaves_on) }), d.lease_until && x.t("lease ends {{date}}", { date: x.date(d.lease_until) })]);
  },
  review: "always",
  agent: {
    when: "Cattle left the herd: sold (sale barn, private treaty, packer), shipped to a feedyard we still own, transferred, leased out or butchered. List every animal that left. When they say 'the culls' or 'cull cows' without tags, call herd_cull_list and use those animals; ask when the number doesn't match.",
    avoid: "Not for deaths (herd.death). Not for a planned sale or truck date in the future (a task). Not for moves between our own places (land).",
    examples: ["sold 5 cull cows at the sale barn", "shipped the steer calves to the feedyard", "B20 leased out to the Moreno place until June", "sold 66 to Bob Jones, truck comes Friday"],
  },
  effects: {
    followUps: (d, _on, subjects) => {
      const first = subjects.find((s) => s.kind === "animal");
      const subject = first ? { subject: { kind: "animal" as const, id: first.id } } : {};
      const out: FollowUp[] = [];
      if (d.leaves_on) out.push({ key: "pickup", title: msg("Sold cattle get picked up"), due_at: d.leaves_on, ...subject });
      if (d.kind === "leased_out" && d.lease_until) out.push({ key: "lease_end", title: msg("Leased-out cattle come home"), due_at: d.lease_until, ...subject });
      return out;
    },
  },
});

const death = defineEvent<DeathData>({
  type: "herd.death",
  label: "Death",
  description: "An animal died or was found dead, with when it was found, what's known about when it died, and the cause.",
  icon: "death",
  tone: "bad",
  subjects: [
    { role: "animal", kind: "animal", label: "Animals", many: true },
    { role: "lot", kind: "group", label: "Lot", help: "For losses in a lot that isn't tagged one by one" },
  ],
  fields: {
    found_on: f.date("Found dead on", { help: "When it was found. When it died goes above, as exact as you know it.", agent: "Date the animal was found dead. Put the date of death in occurred: precision unknown or estimated_date unless they say when it died." }),
    cause: f.enum("Cause", DEATH_CAUSE, { agent: "Only the cause they state or clearly imply; unknown otherwise. Never diagnose." }),
    basis: f.enum("How sure", CAUSE_BASIS, { level: "more" }),
    found_at: f.ref("Where found", "place", { level: "more" }),
    disposal: f.enum("Body disposal", DISPOSAL, { level: "more" }),
    head: f.int("Head", { min: 1, level: "more", help: "Losses in a counted lot without tags" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: needsAnimalsOrHead,
  summary: (e, x) => {
    const d = e.data;
    const cause = d.cause ? x.t(DEATH_CAUSE[d.cause] ?? d.cause) : "";
    return join(x.t("{{what}} died", { what: what(e, x, "animal", d.head) }), [
      cause && (d.basis ? `${cause} (${x.t(CAUSE_BASIS[d.basis as keyof typeof CAUSE_BASIS] ?? d.basis).toLowerCase()})` : cause),
      d.found_on && x.t("found {{date}}", { date: x.date(d.found_on) }), d.found_at && x.t("at {{place}}", { place: x.label(d.found_at) }),
    ]);
  },
  review: "always",
  agent: {
    when: "An animal died or was found dead. Set found_on to the day it was found; set occurred to the death date only as exactly as known (unknown or estimated_date when they just found it).",
    avoid: "Not for a calf born dead (breeding calving outcome). Not for butchering (herd.departure slaughtered). 'Found 42' without dead is herd.found.",
    examples: ["found 88 dead by the creek this morning", "lost a calf to coyotes last night", "had to put down 703D11, broken leg"],
  },
});

const cull = defineEvent<CullData>({
  type: "herd.cull",
  label: "Cull decision",
  description: "Put an animal on the cull list, or take it off. It stays in the herd until it leaves.",
  icon: "cull",
  tone: "warn",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    reason: f.enum("Why", CULL_REASON),
    target: f.date("Sell by", { help: "When you want them gone" }),
    decision: f.enum("Decision", CULL_DECISION, { level: "more", agent: "keep only when they take an animal off the cull list" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    if (d.decision === "keep") return x.t("{{what}} off the cull list", { what: what(e, x) });
    return join(x.t("Cull {{what}}", { what: what(e, x) }), [d.reason && x.t(CULL_REASON[d.reason] ?? d.reason), d.target && x.t("sell by {{date}}", { date: x.date(d.target) })]);
  },
  agent: {
    when: "Someone decided to cull an animal (put it on the cull list) or changed their mind (decision keep). The animal stays until a sale or departure is recorded.",
    avoid: "Not the sale itself (herd.departure).",
    examples: ["cull 703D11, she's broken mouthed", "put 812E04 on the cull list, open again", "keep 23 after all"],
  },
});

const missing = defineEvent<MissingData>({
  type: "herd.missing",
  label: "Missing",
  description: "An animal can't be found. It stays in the herd count until it turns up, dies or leaves.",
  icon: "missing",
  tone: "warn",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    last_seen_on: f.date("Last seen"),
    last_seen_at: f.ref("Last seen in", "place"),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => join(x.t("{{what}} missing", { what: what(e, x) }), [
    e.data.last_seen_on && x.t("last seen {{date}}", { date: x.date(e.data.last_seen_on) }), e.data.last_seen_at && x.t("in {{place}}", { place: x.label(e.data.last_seen_at) }),
  ]),
  agent: {
    when: "An animal can't be found or didn't come in with the rest.",
    examples: ["can't find 133, last saw her in South on Sunday", "two short coming in from North: 17 and 64"],
  },
});

const found = defineEvent<FoundData>({
  type: "herd.found",
  label: "Found",
  description: "A missing animal turned up alive.",
  icon: "found",
  tone: "good",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    found_at: f.ref("Found in", "place"),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => join(x.t("Found {{what}}", { what: what(e, x) }), [e.data.found_at && x.t("in {{place}}", { place: x.label(e.data.found_at) })]),
  agent: {
    when: "A missing animal turned up alive.",
    avoid: "Found dead is herd.death.",
    examples: ["found 133 in the creek trap", "17 showed back up with the neighbor's cows"],
  },
});

const identifier = defineEvent<IdentifierData>({
  type: "herd.identifier",
  label: "Tag or ID change",
  description: "A new tag or number, an added EID or registration, a lost tag, or an ID taken off. Old IDs are kept and still searchable.",
  icon: "tag",
  tone: "herd",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    action: f.enum("What changed", ID_ACTION, { required: true, agent: "retag: a new tag or number replaces the old one (also a same-number replacement tag). add: an extra ID like an EID or registration. lost: the tag fell out and no new one yet. remove: an ID taken off for good." }),
    scheme: f.enum("Kind of ID", ID_SCHEME, { agent: "visual for ear tags (default), eid for 15-digit electronic IDs, registration for association numbers" }),
    new_value: f.text("New tag or ID", { max: 60, agent: "Exactly as said or written, keeping leading zeros" }),
    old_value: f.text("Old tag or ID", { max: 60, agent: "The tag or ID being replaced or removed, exactly as it is now" }),
    issuer: f.text("Color or issuer", { level: "more", help: "Tag color, or the association for registration numbers" }),
    ear: f.enum("Ear", EAR, { level: "more" }),
    reason: f.enum("Why", TAG_REASON, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => {
    const out: Issue[] = [];
    if ((d.action === "retag" || d.action === "add") && !d.new_value?.trim()) out.push({ path: "data.new_value", message: "Write the new tag or ID." });
    if (d.action === "remove" && !d.old_value?.trim()) out.push({ path: "data.old_value", message: "Write the ID to take off." });
    return out;
  },
  summary: (e, x) => {
    const d = e.data;
    const animal = labelOf(e, x, "animal");
    const scheme = x.t(ID_SCHEME[d.scheme ?? "visual"] ?? "Ear tag");
    const reason = d.reason ? x.t(TAG_REASON[d.reason as keyof typeof TAG_REASON] ?? d.reason) : "";
    let lead: string;
    if (d.action === "lost") lead = x.t("Lost tag: {{animal}}", { animal });
    else if (d.action === "remove") lead = x.t("Took {{scheme}} {{value}} off {{animal}}", { scheme, value: d.old_value ?? "", animal });
    else if (d.action === "add") lead = x.t("Added {{scheme}} {{value}} to {{animal}}", { scheme, value: d.new_value ?? "", animal });
    else if (d.old_value && d.new_value && d.old_value.trim().toUpperCase() === d.new_value.trim().toUpperCase()) lead = x.t("Replacement tag for {{animal}}, same number", { animal });
    else if (d.old_value) lead = (d.scheme ?? "visual") === "visual" ? x.t("Retagged {{old}} as {{new}}", { old: d.old_value, new: d.new_value ?? "" }) : x.t("{{scheme}} {{old}} changed to {{new}}", { scheme, old: d.old_value, new: d.new_value ?? "" });
    else lead = x.t("New {{scheme}} {{value}} for {{animal}}", { scheme: scheme.toLowerCase(), value: d.new_value ?? "", animal });
    return join(lead, [reason, d.issuer, d.ear && x.t(EAR[d.ear as keyof typeof EAR] ?? d.ear)]);
  },
  review: "always",
  agent: {
    when: "A tag or ID changed on one animal: retagged with a new number, a replacement tag, an EID or registration added, a lost tag, or an ID taken off. One item per animal. The old tag stays on record and searchable.",
    avoid: "Not for fixing a typo in a brand-new record (update the animal).",
    examples: ["retagged 42 as 4201, she lost her tag", "put EID 840003123456789 in 17", "124 lost her tag"],
  },
});

const weaning = defineEvent<WeaningData>({
  type: "herd.weaning",
  label: "Weaning",
  description: "Calves were separated from their mothers. Weaning weights belong in a weighing.",
  icon: "wean",
  tone: "herd",
  subjects: [{ role: "calf", kind: "animal", label: "Calves", many: true, required: true }],
  fields: {
    method: f.enum("How", WEAN_METHOD),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => join(x.t("Weaned {{what}}", { what: what(e, x, "calf", undefined, "calf") }), [e.data.method && x.t(WEAN_METHOD[e.data.method as keyof typeof WEAN_METHOD] ?? e.data.method)]),
  agent: {
    when: "Calves were weaned (separated from their dams). List every calf. Weights said at the same time are a separate performance weighing.",
    examples: ["weaned the spring calves today, fence-line", "pulled the calves off the fall cows"],
  },
});

const ownership = defineEvent<OwnershipData>({
  type: "herd.ownership",
  label: "Ownership change",
  description: "Who owns an animal or lot, who holds possession, and retained genetic, semen or proceeds rights, with shares.",
  icon: "owner",
  tone: "money",
  permission: "manage",
  subjects: [
    { role: "animal", kind: "animal", label: "Animals", many: true },
    { role: "lot", kind: "group", label: "Lot" },
  ],
  fields: {
    interest: f.enum("Which right", INTEREST, { required: true, agent: "title for who owns them; possession for who has them; genetics, semen or revenue for retained interests. Each right is recorded separately." }),
    our_share: f.number("Our share", { min: 0, max: 100, suffix: "%", help: "The ranch's own share of this right", agent: "Percent the ranch keeps, when said" }),
    holders: f.list("Other holders", f.object("Holder", {
      contact_id: f.ref("Who", "contact", { required: true }),
      share: f.number("Ownership share", { min: 0, max: 100, suffix: "%" }),
    }), { itemLabel: "Holder", agent: "Everyone else who holds this right, with their percent when said" }),
    until: f.date("Until", { level: "more", help: "When this ends, for leases and custody" }),
    basis: f.enum("Backed by", OWNERSHIP_BASIS, { level: "more" }),
    agreement: f.text("Agreement or bill of sale", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d, subjects) => {
    const out: Issue[] = [];
    if (!subjects.some((s) => s.role === "animal" || s.role === "lot")) out.push({ path: "subjects.animal", message: "Pick the animals or the lot." });
    const total = (d.holders ?? []).reduce((n, h) => n + (h.share ?? 0), 0) + (d.our_share ?? 0);
    if (total > 100.0001) out.push({ path: "data.holders", message: "Shares add up to more than 100%." });
    return out;
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = (d.holders ?? []).map((h) => (h.share !== undefined ? `${x.label(h.contact_id)} ${h.share}%` : x.label(h.contact_id)));
    if (d.our_share !== undefined) parts.unshift(x.t("ours {{share}}%", { share: d.our_share }));
    const subject = e.subjects.some((s) => s.role === "lot") ? labelOf(e, x, "lot") : what(e, x);
    return join(x.t("{{right}}: {{holders}}", { right: x.t(INTEREST[d.interest ?? "title"]), holders: x.list(parts) }), [subject]);
  },
  review: "always",
  agent: {
    when: "Who owns an animal or lot changed or was stated: co-owners and shares, someone else's cattle, possession or custody, retained genetic or semen interests, shares of proceeds. One item per right.",
    avoid: "A sale itself is herd.departure; record retained rights alongside it.",
    examples: ["Mesa Cattle owns half the yearling lot", "sold bull B20 but kept a quarter semen interest", "the Davis heifers are Bob Jones's, we just run them"],
  },
});

const count = defineEvent<CountData>({
  type: "herd.count",
  label: "Head count",
  description: "Cattle counted in a pasture, pen, group or lot, against how many should be there.",
  icon: "count",
  tone: "herd",
  subjects: [
    { role: "place", kind: "place", label: "Place" },
    { role: "group", kind: "group", label: "Group or lot" },
  ],
  fields: {
    counted: f.int("Counted", { required: true, min: 0, suffix: "head" }),
    expected: f.int("Should be", { min: 0, suffix: "head", help: "How many the records say, or how many you expected", agent: "Only when said, or from herd_on_record (mark evidence calculated)" }),
    class: f.enum("What kind", ANIMAL_CLASS, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (_d, subjects) => subjects.some((s) => s.role === "place" || s.role === "group") ? [] : [{ path: "subjects.place", message: "Pick where you counted, or the group or lot." }],
  summary: (e, x) => {
    const d = e.data;
    const where = labelOf(e, x, "group") || labelOf(e, x, "place");
    const diff = d.expected === undefined ? undefined : d.counted - d.expected;
    return join(x.t("Counted {{counted}} in {{where}}", { counted: d.counted, where }), [
      d.expected !== undefined && x.t("should be {{expected}}", { expected: d.expected }),
      diff !== undefined && (diff === 0 ? x.t("matches") : diff < 0 ? x.count(-diff, "{{count}} short", "{{count}} short") : x.count(diff, "{{count}} over", "{{count}} over")),
    ]);
  },
  agent: {
    when: "A head count of a pasture, pen, group or lot, with how many should be there when said. A count that comes up short is a count, not a death or missing record, unless they name the animals.",
    examples: ["counted 112 head in North, should be 115", "39 in the yearling lot this morning"],
  },
});

const lot = defineEvent<LotData>({
  type: "herd.lot",
  label: "Lot change",
  description: "Tagged animals sorted into or out of a production or ownership lot, or head split off into another lot.",
  icon: "lot",
  tone: "herd",
  subjects: [
    { role: "animal", kind: "animal", label: "Animals", many: true },
    { role: "lot", kind: "group", label: "Lot", required: true },
    { role: "to_lot", kind: "group", label: "To lot", help: "Where they went, when they moved to another lot" },
  ],
  fields: {
    action: f.enum("Change", LOT_ACTION, { required: true, agent: "join or leave for tagged animals; move to split head off into another lot (to_lot), tagged or counted" }),
    head: f.int("Head", { min: 1, help: "All the head that moved, tagged or not", agent: "Head moved to the other lot when not every animal is listed" }),
    already_counted: f.bool("Already counted in the lot", { help: "They were part of the lot's head count and just got tagged", agent: "true when untagged head in the lot were tagged, so the lot's head count doesn't grow" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d, subjects) => {
    const out: Issue[] = [];
    const animals = subjects.some((s) => s.role === "animal");
    const to = subjects.some((s) => s.role === "to_lot");
    if ((d.action ?? "join") === "move") {
      if (!to) out.push({ path: "subjects.to_lot", message: "Pick the lot they went to." });
      if (!animals && typeof d.head !== "number") out.push({ path: "data.head", message: "Say how many head moved, or pick the animals." });
      const from = subjects.find((s) => s.role === "lot")?.id;
      if (to && from && subjects.find((s) => s.role === "to_lot")?.id === from) out.push({ path: "subjects.to_lot", message: "Pick a different lot." });
    } else if (!animals) out.push({ path: "subjects.animal", message: "Pick the animals." });
    return out;
  },
  summary: (e, x) => {
    const d = e.data;
    const name = labelOf(e, x, "lot");
    const action = d.action ?? "join";
    const lead = action === "move" ? x.t("{{what}} from {{lot}} to {{to}}", { what: what(e, x, "animal", d.head), lot: name, to: labelOf(e, x, "to_lot") })
      : action === "join" ? x.t("{{what}} into {{lot}}", { what: what(e, x), lot: name }) : x.t("{{what}} out of {{lot}}", { what: what(e, x), lot: name });
    return join(lead, [d.already_counted && x.t("already counted, now tagged")]);
  },
  agent: {
    when: "Tagged animals sorted into or out of a lot (a group with kind lot), untagged lot cattle that just got tags (already_counted true), or head split off into another lot (move, with to_lot and head). A split keeps both lots' history; never invent tags.",
    avoid: "Ordinary groups use a membership change. Moving pastures is land.",
    examples: ["put 2511 and 2512 in the yearling lot", "tagged 5 of the yearling lot steers: Y09 through Y13", "split 40 head off the Davis lot into a new lot for the Moreno pasture"],
  },
});

// ---------------------------------------------------------------- facets

const HERD_EVENTS = ["herd.arrival", "herd.departure", "herd.death", "herd.cull", "herd.missing", "herd.found", "herd.identifier", "herd.weaning", "herd.ownership", "herd.lot"];

export const herdFacet = defineFacet<HerdFacet>({
  id: "herd",
  subject: "animal",
  version: 1,
  eventTypes: HERD_EVENTS,
  reduce: (events) => reduceHerd(events),
  project(s): AnimalProjection {
    if (!s) return {};
    const out: AnimalProjection = {};
    if (s.status) out.status = s.status;
    if (s.lot_id !== undefined) out.lot_id = s.lot_id;
    return out;
  },
  badges(s, { today }): Badge[] {
    const out: Badge[] = [];
    if (s.status === "missing") out.push({ label: msg("Missing"), tone: "warn", icon: "missing" });
    if (s.on_cull_list) out.push({ label: msg("Cull"), tone: "warn", icon: "cull" });
    if (s.leased === "in") out.push({ label: msg("Leased in"), tone: "info", icon: "handshake" });
    if (s.leased === "out") out.push({ label: msg("Leased out"), tone: "info", icon: "handshake" });
    if (s.needs_tag) out.push({ label: msg("Needs a tag"), tone: "warn", icon: "tag" });
    if (s.status === "sold" && s.departure?.leaves_on && s.departure.leaves_on >= today) out.push({ label: msg("Leaves {{date}}", { date: s.departure.leaves_on }), tone: "sale", icon: "truck" });
    return out;
  },
  facts(s): Fact[] {
    const out: Fact[] = [];
    if (s.arrival) out.push({ label: "Came in", icon: "arrive", value: s.arrival.on ? msg(ARRIVAL_FACT[s.arrival.kind] ?? "Bought {{date}}", { date: s.arrival.on }) : msg(ARRIVAL_KIND[s.arrival.kind] ?? "Bought") });
    if (s.owner_ids?.length) {
      const n = s.owner_ids.length;
      out.push({ label: "Owners", icon: "owner", value: s.our_share ? (n === 1 ? msg("Shared with 1 other owner") : msg("Shared with {{count}} other owners", { count: n })) : msg("Owned by someone else") });
    }
    if (s.on_cull_list && s.cull) out.push({ label: "Cull list", icon: "cull", tone: "warn", value: s.cull.reason ? msg(CULL_REASON[s.cull.reason]) : msg("On the cull list") });
    if (s.departure && s.status !== "active" && s.status !== "missing") out.push({ label: "Left the herd", icon: "truck", value: msg(DEPARTURE_FACT[s.departure.reason] ?? "Sold {{date}}", { date: s.departure.on ?? "?" }) });
    if (s.death) out.push({ label: "Cause of death", icon: "death", value: msg(DEATH_CAUSE[s.death.cause ?? "unknown"] ?? "Not known") });
    if (s.weaned_on) out.push({ label: "Weaning", icon: "wean", value: msg("Weaned {{date}}", { date: s.weaned_on }) });
    return out;
  },
  filters: {
    on_cull_list: { label: "On the cull list", type: "boolean" },
    needs_tag: { label: "Needs a new tag", type: "boolean" },
    arrival_kind: { label: "How they came", type: "enum", options: ARRIVAL_KIND },
    leased: { label: "Leased", type: "enum", options: { in: "Leased in", out: "Leased out" } },
    weaned: { label: "Weaned", type: "boolean" },
  },
});

export const lotFacet = defineFacet<LotFacet>({
  id: "herd",
  subject: "group",
  version: 1,
  eventTypes: ["herd.arrival", "herd.departure", "herd.death", "herd.count", "herd.ownership"],
  reduce: (events) => reduceLot(events),
  facts(s): Fact[] {
    const out: Fact[] = [];
    if (s.last_count) out.push({ label: "Last count", icon: "count", value: msg("{{counted}} on {{date}}", { counted: s.last_count.counted, date: s.last_count.on ?? "?" }) });
    return out;
  },
});

export const placeCountFacet = defineFacet<PlaceFacet>({
  id: "herd",
  subject: "place",
  version: 1,
  eventTypes: ["herd.count"],
  reduce: (events) => reducePlace(events),
  badges(s, { today }): Badge[] {
    const diff = discrepancy(s.last_count);
    if (!diff || !s.last_count?.on) return [];
    const ageDays = (Date.parse(`${today}T12:00:00Z`) - Date.parse(`${s.last_count.on}T12:00:00Z`)) / 86_400_000;
    if (ageDays > 30) return [];
    return [diff < 0 ? { label: msg("{{count}} short", { count: -diff }), tone: "warn", icon: "count" } : { label: msg("{{count}} over", { count: diff }), tone: "warn", icon: "count" }];
  },
  facts(s): Fact[] {
    if (!s.last_count) return [];
    return [{ label: "Last count", icon: "count", value: msg("{{counted}} on {{date}}", { counted: s.last_count.counted, date: s.last_count.on ?? "?" }) }];
  },
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "herd",
  label: "Herd",
  description: "Identity, tags, groups, lifecycle, ownership and head counts.",
  events: [arrival, departure, death, cull, missing, found, identifier, weaning, ownership, count, lot],
  facets: [herdFacet, lotFacet, placeCountFacet],
  jobs: [
    {
      type: "herd.shipping", label: "Ship cattle", description: "Go down a list of cattle to sell or ship and record each one leaving.",
      icon: "truck", tone: "sale", workspace: "herd", subjectKind: "animal", itemEvent: "herd.departure",
      fields: { ship_on: f.date("Shipping day"), buyer_id: f.ref("Buyer or sale barn", "contact"), destination: f.text("Where they're going") },
      agent: { when: "Plan to sell or ship a set of cattle (the culls, the steer calves) on a day; the worklist records each departure." },
    },
    {
      type: "herd.retag_round", label: "Retag round", description: "Work through cattle that need new tags or EIDs and record each one.",
      icon: "tag", tone: "herd", workspace: "herd", subjectKind: "animal", itemEvent: "herd.identifier",
      agent: { when: "Plan to retag or put EIDs in a set of cattle." },
    },
    {
      type: "herd.count_round", label: "Gather and count", description: "Count every pasture or pen on a gather and compare with the records.",
      icon: "count", tone: "herd", workspace: "herd", subjectKind: "place", itemEvent: "herd.count",
      agent: { when: "Plan a count of several pastures or pens." },
    },
  ],
  workspaces: [{
    id: "herd", label: "Herd inventory",
    description: "Head counts by class and place on any date, missing cattle, the cull list, lots, tags and duplicates.",
    icon: "count", group: "operations",
    keywords: ["inventory", "head count", "count", "missing", "cull", "cull list", "dead", "death", "lot", "lots", "tags", "retag", "duplicate", "sold", "bought", "purchase", "reconcile", "ownership", "saved list"],
    keywordsEs: ["inventario", "conteo", "cabezas", "faltante", "perdido", "desecho", "muerte", "muerto", "lote", "aretes", "rearetar", "duplicado", "venta", "compra", "conciliar", "propiedad", "lista"],
    presets: ["basic", "commercial", "feedlot"],
  }],
  attributes: {
    group: { lot_purpose: f.enum("Lot type", LOT_PURPOSE, { level: "more" }) },
  },
  messages: [
    ...Object.values(ARRIVAL_VERB), ...Object.values(DEPARTURE_VERB), ...Object.values(ARRIVAL_FACT), ...Object.values(DEPARTURE_FACT),
    ...Object.values(CLASS_COUNT).flat(),
    "Came in", "Owners", "Cull list", "Left the herd", "Cause of death", "Weaning", "Last count",
    // Issue messages from the event validators above, translated where the web shows them.
    "Pick the animals, or a lot and how many head.", "Write the new tag or ID.", "Write the ID to take off.", "Pick the animals or the lot.",
    "Shares add up to more than 100%.", "Pick where you counted, or the group or lot.", "Pick the lot they went to.", "Say how many head moved, or pick the animals.", "Pick a different lot.", "Pick the animals.", "Head can't be fewer than the animals picked.",
    // Labels the web module registers for sections and actions.
    "Herd status", "Mark found", "Record sale or shipment", "Retag", "Record death", "Cull", "Take off the cull list", "Mark missing", "Lot", "Live list", "Head counts",
  ],
});
